import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  IconButton,
  Skeleton,
  Pagination,
  Dialog,
  DialogContent,
  Autocomplete
} from '@mui/material';
import {
  Search,
  FilterList,
  WhatsApp,
  Phone,
  Store,
  Person,
  Close,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { serviceService } from '../services/serviceService';
import ImageCard from '../components/ImageCard';

const Services = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [categoryServices, setCategoryServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  
  // États pour la modal d'images
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [validImages, setValidImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // État pour suivre l'index de l'image actuelle pour chaque service dans les cartes
  const [serviceImageIndices, setServiceImageIndices] = useState({});

  useEffect(() => {
    fetchCategoryServices();
    fetchServices();
  }, [search, category, sortBy]);

  // Reset de la page quand on change de recherche, catégorie ou tri
  useEffect(() => {
    setCurrentPage(1);
  }, [search, category, sortBy]);

  const fetchCategoryServices = async () => {
    try {
      setLoadingCategories(true);
      const response = await serviceService.getCategoryServices();
      if (response.success) {
        setCategoryServices(response.categories || []);
      }
    } catch (error) {
      console.error('Error fetching category services:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchServices = async () => {
    try {
      setLoading(true);
      const params = {
        isActive: true,
        isAvailable: true
      };
      
      const response = await serviceService.getServices(params);
      if (response.success) {
        setServices(response.services || []);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrer et trier les services
  const filteredAndSortedServices = useMemo(() => {
    let filtered = services;

    // Filtrer par recherche
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(service =>
        service.name?.toLowerCase().includes(searchLower) ||
        service.description?.toLowerCase().includes(searchLower) ||
        service.user?.name?.toLowerCase().includes(searchLower) ||
        service.boutique?.name?.toLowerCase().includes(searchLower) ||
        service.categories?.some(cat => 
          cat.categorieService?.libelle?.toLowerCase().includes(searchLower) ||
          cat.libelle?.toLowerCase().includes(searchLower)
        )
      );
    }

    // Filtrer par catégorie
    if (category) {
      filtered = filtered.filter(service =>
        service.categories?.some(cat => 
          cat.categorieService?.id === category || cat.id === category
        )
      );
    }

    // Trier
    const sorted = [...filtered];
    switch (sortBy) {
      case 'price-asc':
        sorted.sort((a, b) => {
          const priceA = a.price ? parseFloat(a.price) : 0;
          const priceB = b.price ? parseFloat(b.price) : 0;
          return priceA - priceB;
        });
        break;
      case 'price-desc':
        sorted.sort((a, b) => {
          const priceA = a.price ? parseFloat(a.price) : 0;
          const priceB = b.price ? parseFloat(b.price) : 0;
          return priceB - priceA;
        });
        break;
      case 'name-asc':
        sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'name-desc':
        sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        break;
      case 'newest':
      default:
        sorted.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dateB - dateA;
        });
        break;
    }

    return sorted;
  }, [services, search, category, sortBy]);

  // Calcul de la pagination
  const totalPages = Math.ceil(filteredAndSortedServices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentServices = filteredAndSortedServices.slice(startIndex, endIndex);

  // Fonction pour changer de page
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    // Scroll vers le haut de la page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fonction helper pour filtrer les images valides
  const filterValidImages = useCallback((images) => {
    return images.filter(img => {
      return img && typeof img === 'string' && img.trim() !== '';
    });
  }, []);

  // Fonctions pour naviguer entre les images dans les cartes
  const handleServiceImageNext = (e, serviceId, totalImages) => {
    e.stopPropagation();
    setServiceImageIndices(prev => ({
      ...prev,
      [serviceId]: ((prev[serviceId] || 0) + 1) % totalImages
    }));
  };

  const handleServiceImagePrev = (e, serviceId, totalImages) => {
    e.stopPropagation();
    setServiceImageIndices(prev => ({
      ...prev,
      [serviceId]: ((prev[serviceId] || 0) - 1 + totalImages) % totalImages
    }));
  };

  const handleImageClick = (service) => {
    // Vérifier qu'il y a des images avant d'ouvrir le modal
    const images = service?.images || [];
    
    // Filtrer les images valides
    const validImagesArray = filterValidImages(images);
    
    // Ne pas ouvrir si aucune image
    if (validImagesArray.length === 0) {
      return;
    }
    
    // Précharger toutes les images pour éviter le clignotement
    validImagesArray.forEach(imgSrc => {
      if (imgSrc) {
        const img = new Image();
        img.src = imgSrc;
      }
    });
    
    setSelectedService(service);
    setValidImages(validImagesArray);
    setCurrentImageIndex(0);
    setImageModalOpen(true);
  };

  const handleCloseModal = useCallback(() => {
    setImageModalOpen(false);
    setSelectedService(null);
    setValidImages([]);
    setCurrentImageIndex(0);
  }, []);

  const handleNextImage = useCallback(() => {
    if (validImages.length === 0) return;
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % validImages.length);
  }, [validImages.length]);

  const handlePreviousImage = useCallback(() => {
    if (validImages.length === 0) return;
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + validImages.length) % validImages.length);
  }, [validImages.length]);

  const handleThumbnailClick = useCallback((index) => {
    if (index >= 0 && index < validImages.length && index !== currentImageIndex) {
      setCurrentImageIndex(index);
    }
  }, [validImages.length, currentImageIndex]);

  // Corriger l'index si invalide quand les images valides changent
  useEffect(() => {
    if (validImages.length > 0 && currentImageIndex >= validImages.length) {
      setCurrentImageIndex(0);
    }
  }, [validImages.length, currentImageIndex]);

  // Mémoriser les images pour éviter les recalculs
  const modalImages = useMemo(() => {
    return validImages.length > 0 ? validImages : (selectedService?.images || []);
  }, [validImages, selectedService]);

  // Mémoriser l'index sûr
  const safeIndex = useMemo(() => {
    if (modalImages.length === 0) return 0;
    return currentImageIndex >= modalImages.length ? 0 : currentImageIndex;
  }, [modalImages.length, currentImageIndex]);

  const ServiceSkeleton = () => (
    <Card>
      <Skeleton variant="rectangular" height={200} />
      <CardContent>
        <Skeleton variant="text" height={32} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="rectangular" height={36} sx={{ mt: 2 }} />
      </CardContent>
    </Card>
  );

  const ImageModal = () => {
    const currentImage = modalImages[safeIndex];

    return (
      <Dialog
        open={imageModalOpen}
        onClose={handleCloseModal}
        maxWidth={false}
        keepMounted={false}
        disableEscapeKeyDown={false}
        PaperProps={{
          sx: {
            width: '85vw',
            maxWidth: '900px',
            maxHeight: '85vh',
            bgcolor: 'rgba(0, 0, 0, 0.95)',
            position: 'relative',
            margin: 0,
            transition: 'none !important'
          }
        }}
        BackdropProps={{
          sx: {
            transition: 'none !important',
            animation: 'none !important',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            opacity: '1 !important'
          }
        }}
        sx={{
          '& .MuiDialog-container': {
            transition: 'none !important',
            animation: 'none !important'
          },
          '& .MuiDialog-root': {
            transition: 'none !important',
            animation: 'none !important'
          },
          '& .MuiBackdrop-root': {
            transition: 'none !important',
            animation: 'none !important',
            opacity: '1 !important'
          },
          '& .MuiDialog-paper': {
            transition: 'none !important',
            animation: 'none !important'
          },
          '& .MuiDialog-paperScrollBody': {
            transition: 'none !important',
            animation: 'none !important'
          }
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '70vh',
            maxHeight: '75vh',
            overflow: 'hidden',
            transition: 'none !important',
            '&.MuiDialogContent-root': {
              transition: 'none !important'
            }
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleCloseModal();
            }}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            <Close />
          </IconButton>

          {/* Previous button */}
          {modalImages.length > 1 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handlePreviousImage();
              }}
              disabled={modalImages.length <= 1}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                bgcolor: 'rgba(255,255,255,0.9)',
                color: 'text.primary',
                '&:hover': {
                  bgcolor: 'white'
                },
                '&:active': {
                  transform: 'translateY(-50%) scale(0.95)'
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(255, 255, 255, 0.5)',
                  color: 'rgba(0, 0, 0, 0.3)'
                },
                transition: 'all 0.15s ease'
              }}
            >
              <ChevronLeft />
            </IconButton>
          )}

          {/* Next button */}
          {modalImages.length > 1 && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleNextImage();
              }}
              disabled={modalImages.length <= 1}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 10,
                bgcolor: 'rgba(255,255,255,0.9)',
                color: 'text.primary',
                '&:hover': {
                  bgcolor: 'white'
                },
                '&:active': {
                  transform: 'translateY(-50%) scale(0.95)'
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(255, 255, 255, 0.5)',
                  color: 'rgba(0, 0, 0, 0.3)'
                },
                transition: 'all 0.15s ease'
              }}
            >
              <ChevronRight />
            </IconButton>
          )}

          {/* Image container */}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              position: 'relative',
              userSelect: 'none'
            }}
          >
            {currentImage ? (
              <Box
                component="img"
                key={`img-${safeIndex}`}
                src={currentImage}
                alt={selectedService?.name || 'Service image'}
                loading="eager"
                onError={(e) => {
                  if (!e.target.src.includes('no_media')) {
                    e.target.src = '/img/no_media.jpg';
                  }
                }}
                onLoad={(e) => {
                  // Précharger les images adjacentes pour éviter le clignotement
                  if (modalImages.length > 1) {
                    const nextIndex = (safeIndex + 1) % modalImages.length;
                    const prevIndex = (safeIndex - 1 + modalImages.length) % modalImages.length;
                    
                    [nextIndex, prevIndex].forEach(idx => {
                      if (modalImages[idx] && modalImages[idx] !== currentImage) {
                        const preloadImg = new Image();
                        preloadImg.src = modalImages[idx];
                      }
                    });
                  }
                }}
                draggable={false}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  transition: 'opacity 0.15s ease-in-out',
                  userSelect: 'none',
                  pointerEvents: 'auto',
                  imageRendering: 'auto',
                  display: 'block',
                  visibility: 'visible'
                }}
              />
            ) : (
              <Box
                component="img"
                src="/img/no_media.jpg"
                alt="No image"
                draggable={false}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain'
                }}
              />
            )}
          </Box>

          {/* Image counter */}
          {modalImages.length > 1 && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.7)',
                color: 'white',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 500,
                zIndex: 10
              }}
            >
              {safeIndex + 1} / {modalImages.length}
            </Box>
          )}
        </DialogContent>

        {/* Miniatures */}
        {modalImages.length > 1 && (
          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              gap: 0.75,
              overflowX: 'auto',
              borderTop: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              width: '100%',
              '&::-webkit-scrollbar': {
                height: '6px'
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '3px'
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '3px',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.5)'
                }
              }
            }}
          >
            {modalImages.map((image, index) => {
              const isActive = safeIndex === index;
              return (
                <Box
                  key={`thumb-${index}-${image?.substring(0, 30) || index}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleThumbnailClick(index);
                  }}
                  sx={{
                    minWidth: 70,
                    width: 70,
                    height: 50,
                    borderRadius: 1,
                    overflow: 'hidden',
                    cursor: isActive ? 'default' : 'pointer',
                    border: isActive
                      ? '2px solid #1976d2'
                      : '2px solid transparent',
                    opacity: isActive ? 1 : 0.7,
                    flexShrink: 0,
                    '&:hover': { 
                      opacity: isActive ? 1 : 1,
                      borderColor: isActive ? '#1976d2' : 'rgba(255, 255, 255, 0.5)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Box
                    component="img"
                    src={image}
                    alt={`${selectedService?.name || 'Image'} ${index + 1}`}
                    onError={(e) => {
                      if (!e.target.src.includes('no_media')) {
                        e.target.src = '/img/no_media.jpg';
                      }
                    }}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      pointerEvents: 'none'
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Dialog>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
          Nos services
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Découvrez notre sélection de services de qualité
        </Typography>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 4, p: { xs: 2, sm: 3 } }}>
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={2} 
          alignItems="center"
          justifyContent="center"
        >
          <Box 
            sx={{ 
              flexGrow: 1, 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2, 
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'center',
              width: { xs: '100%', sm: 'auto' },
              maxWidth: { xs: '100%', md: 1200 }
            }}
          >
            <TextField
              placeholder="Rechercher des services..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />
              }}
              sx={{ 
                flexGrow: 1, 
                maxWidth: { xs: '100%', sm: 400 },
                width: { xs: '100%', sm: 'auto' }
              }}
              fullWidth={false}
            />
            <Autocomplete
              options={[{ id: '', libelle: 'Toutes les catégories' }, ...categoryServices]}
              getOptionLabel={(option) => option.libelle || option.name || ''}
              value={
                category 
                  ? (categoryServices.find(cat => cat.id === category) || null)
                  : { id: '', libelle: 'Toutes les catégories' }
              }
              onChange={(event, newValue) => {
                if (!newValue || newValue.id === '') {
                  setCategory('');
                } else {
                  setCategory(newValue.id || '');
                }
              }}
              loading={loadingCategories}
              disabled={loadingCategories}
              isOptionEqualToValue={(option, value) => {
                if (option.id === '' && value.id === '') return true;
                return option.id === value.id;
              }}
              sx={{ 
                minWidth: { xs: '100%', sm: 200, md: 250 },
                width: { xs: '100%', sm: 'auto' },
                '& .MuiAutocomplete-inputRoot': {
                  width: '100% !important',
                  minWidth: '100% !important'
                },
                '& .MuiAutocomplete-input': {
                  width: '100% !important'
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Catégorie"
                  placeholder={loadingCategories ? "Chargement des catégories..." : "Sélectionnez une catégorie"}
                  sx={{ 
                    width: '100%',
                    minWidth: '100%',
                    maxWidth: '100%',
                    '& .MuiInputBase-root': {
                      width: '100% !important',
                      minWidth: '100% !important'
                    },
                    '& .MuiInputBase-input': {
                      width: '100% !important',
                      minWidth: '100% !important'
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body1">
                    {option.libelle || option.name}
                  </Typography>
                </Box>
              )}
              noOptionsText={
                loadingCategories
                  ? "Chargement des catégories..."
                  : "Aucune catégorie trouvée"
              }
            />
            <FormControl 
              sx={{ 
                minWidth: { xs: '100%', sm: 200, md: 250 },
                width: { xs: '100%', sm: 'auto' }
              }}
              fullWidth={false}
            >
              <InputLabel>Trier par</InputLabel>
              <Select
                value={sortBy}
                label="Trier par"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="newest">Plus récents</MenuItem>
                <MenuItem value="price-asc">Prix croissant</MenuItem>
                <MenuItem value="price-desc">Prix décroissant</MenuItem>
                <MenuItem value="name-asc">Nom (A-Z)</MenuItem>
                <MenuItem value="name-desc">Nom (Z-A)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </Card>

      {/* Services Grid */}
      {loading ? (
        <Grid container spacing={3} sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gridAutoRows: '1fr'
        }}>
          {[...Array(9)].map((_, index) => (
            <Grid item xs={12} sm={6} lg={3} key={index}>
              <ServiceSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : filteredAndSortedServices.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Aucun service trouvé
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {search || category 
              ? 'Essayez de modifier vos critères de recherche' 
              : 'Aucun service disponible pour le moment'}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              {filteredAndSortedServices.length} service{filteredAndSortedServices.length > 1 ? 's' : ''} trouvé{filteredAndSortedServices.length > 1 ? 's' : ''}
              {totalPages > 1 && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  (Page {currentPage} sur {totalPages})
                </Typography>
              )}
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gridAutoRows: '1fr' }}>
            {currentServices.map((service) => {
              const serviceCategories = service.categories?.map(c => 
                c.categorieService?.libelle || c.libelle
              ) || [];
              
              // Si l'utilisateur a une boutique, utiliser le numéro de la boutique, sinon utiliser le numéro de l'utilisateur
              const whatsappNumber = service.boutique 
                ? (service.boutique.whatsapp || service.boutique.phone)
                : service.user?.phone;
              const phoneNumber = service.boutique 
                ? service.boutique.phone
                : service.user?.phone;

              return (
                <Grid item xs={12} sm={6} lg={3} key={service.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: 2
                      }
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        height: 200,
                        overflow: 'hidden',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleImageClick(service)}
                    >
                      {(() => {
                        // S'assurer que service.images est un tableau et filtrer les valeurs vides
                        const serviceImages = Array.isArray(service.images) 
                          ? service.images.filter(img => img && typeof img === 'string' && img.trim() !== '')
                          : [];
                        const currentIndex = serviceImageIndices[service.id] || 0;
                        const hasMultipleImages = serviceImages.length > 1;
                        // Utiliser l'image à l'index courant, ou la première, ou une image par défaut
                        const currentImage = serviceImages.length > 0 
                          ? (serviceImages[currentIndex] || serviceImages[0])
                          : '/img/no_media.jpg';
                        
                        return (
                          <>
                            <ImageCard
                              src={currentImage}
                              alt={service.name}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                            />
                            
                            {/* Navigation entre images */}
                            {hasMultipleImages && (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleServiceImagePrev(e, service.id, serviceImages.length)}
                                  sx={{
                                    position: 'absolute',
                                    left: 8,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255,255,255,0.9)',
                                    '&:hover': { bgcolor: 'white' },
                                    zIndex: 2
                                  }}
                                >
                                  <ChevronLeft fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleServiceImageNext(e, service.id, serviceImages.length)}
                                  sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255,255,255,0.9)',
                                    '&:hover': { bgcolor: 'white' },
                                    zIndex: 2
                                  }}
                                >
                                  <ChevronRight fontSize="small" />
                                </IconButton>
                                {/* Indicateur du nombre d'images */}
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    bottom: 8,
                                    right: 8,
                                    bgcolor: 'rgba(0,0,0,0.7)',
                                    color: 'white',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    zIndex: 2
                                  }}
                                >
                                  {currentIndex + 1} / {serviceImages.length}
                                </Box>
                              </>
                            )}
                          </>
                        );
                      })()}
                      {serviceCategories.length > 0 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            maxWidth: 'calc(100% - 16px)',
                            maxHeight: 'calc(100% - 16px)',
                            overflow: 'auto',
                            zIndex: 2,
                            '&::-webkit-scrollbar': {
                              width: '4px',
                              height: '4px'
                            },
                            '&::-webkit-scrollbar-track': {
                              background: 'rgba(255, 255, 255, 0.1)',
                              borderRadius: '2px'
                            },
                            '&::-webkit-scrollbar-thumb': {
                              background: 'rgba(255, 255, 255, 0.3)',
                              borderRadius: '2px',
                              '&:hover': {
                                background: 'rgba(255, 255, 255, 0.5)'
                              }
                            }
                          }}
                        >
                          {serviceCategories.map((cat, idx) => (
                            <Chip
                              key={idx}
                              label={cat}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                fontSize: '0.7rem',
                                height: 20,
                                whiteSpace: 'nowrap'
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        {service.name}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 2,
                          flexGrow: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {service.description || 'Aucune description disponible'}
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        {service.price ? (
                          <Typography variant="h6" color="primary.main" sx={{ fontWeight: 700 }}>
                            {parseFloat(service.price || 0).toLocaleString('fr-FR', { useGrouping: true, minimumFractionDigits: 0, maximumFractionDigits: 0 })} FCFA
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Sur demande
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        {service.user && (
                          <Chip
                            icon={<Person />}
                            label={service.user.name}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        )}
                        {service.boutique && (
                          <Chip
                            icon={<Store />}
                            label={service.boutique.name}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        )}
                      </Box>

                      <Stack direction="row" spacing={1}>
                        {phoneNumber && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Phone />}
                            onClick={(e) => {
                              e.stopPropagation();
                              const cleanPhone = phoneNumber.replace(/[^0-9]/g, '');
                              window.open(`tel:${cleanPhone}`, '_self');
                            }}
                            fullWidth
                            sx={{ flex: 1 }}
                          >
                            Appeler
                          </Button>
                        )}
                        {whatsappNumber && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<WhatsApp />}
                            onClick={(e) => {
                              e.stopPropagation();
                              const cleanWhatsapp = whatsappNumber.replace(/[^0-9]/g, '');
                              const serviceName = service.name || 'ce service';
                              const providerName = service.user?.name || service.boutique?.name || 'vous';
                              const message = `Bonjour, je suis intéressé par le service "${serviceName}" de ${providerName}`;
                              window.open(`https://wa.me/${cleanWhatsapp}?text=${encodeURIComponent(message)}`, '_blank');
                            }}
                            fullWidth
                            sx={{ 
                              flex: 1,
                              bgcolor: '#25D366', 
                              '&:hover': { bgcolor: '#20BA5A' } 
                            }}
                          >
                            WhatsApp
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontSize: '1rem',
                    fontWeight: 500
                  }
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* Image Modal */}
      <ImageModal />
    </Container>
  );
};

export default Services;
